import type { ComponentPropsWithRef, MouseEvent, ReactNode } from 'react';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';

import { cn } from '../../../lib/cn';
import { Label } from './Label';
import type { ControlSize } from './controlStyles';

/* =============================================================================
   Field — the wrapper every input in the library renders through.
   -----------------------------------------------------------------------------
   It owns the four rows (label / description / control / message) and, with
   them, the wiring the spec makes mandatory:

     - `aria-describedby` points at the description and the message, so the
       message is read as the field's description, not as loose text.
     - `aria-invalid` is set on the control by the control, from the same
       resolved error this Field is rendering.
     - The message row carries `role="alert"` (error) or `aria-live="polite"`
       (success), which is the live region the blocking-submission rule
       announces through.
   ============================================================================= */

/** The id the description row renders with. */
export function fieldDescriptionId(id: string): string {
  return `${id}-description`;
}

/** The id the message row renders with. */
export function fieldMessageId(id: string): string {
  return `${id}-message`;
}

/**
 * The `aria-describedby` value a control should carry, in reading order:
 * the description first, the message second. `undefined` when there is neither,
 * so the attribute is omitted rather than rendered empty.
 */
export function fieldDescribedBy(id: string, { description, message }: { description?: boolean; message?: boolean } = {}): string | undefined {
  const ids = [description ? fieldDescriptionId(id) : null, message ? fieldMessageId(id) : null].filter(
    (value): value is string => value !== null,
  );
  return ids.length > 0 ? ids.join(' ') : undefined;
}

export type FieldMessageTone = 'error' | 'success' | 'description';

export interface FieldMessageProps extends Omit<ComponentPropsWithRef<'p'>, 'children'> {
  tone?: FieldMessageTone;
  children?: ReactNode;
}

/**
 * The message line under a control. An error is an alert (assertive, announced
 * the moment it appears) and carries an icon as well as the danger colour, so
 * the state is never signalled by colour alone.
 *
 * The error tone shakes once on appearance. `global.css` collapses every
 * animation to 0.01ms under `prefers-reduced-motion`, and `motion-reduce` is
 * declared here too so the intent survives a change to that policy.
 */
export function FieldMessage({ tone = 'error', className, children, ...rest }: FieldMessageProps) {
  const isError = tone === 'error';
  const isSuccess = tone === 'success';

  return (
    <p
      {...rest}
      // An error is an `alert`, which is announced as it is inserted. Success is
      // a `status` — the conventional pairing; a polite region carrying its text
      // in at insertion time is announced by some screen readers and not others,
      // which is why the inline-save state is never the only feedback a save
      // produces.
      role={isError ? 'alert' : isSuccess ? 'status' : undefined}
      className={cn(
        'flex items-start gap-1.5 text-xs leading-snug',
        isError && 'animate-[dhd-shake_var(--dur-slow)_ease-out] text-danger motion-reduce:animate-none',
        isSuccess && 'text-success',
        tone === 'description' && 'text-muted',
        className,
      )}
    >
      {isError ? (
        <IconAlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      ) : isSuccess ? (
        <IconCheck className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      ) : null}
      <span className="min-w-0">{children}</span>
    </p>
  );
}

export interface FieldProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  /** The control's id. The label, description and message rows all hang off it. */
  id: string;
  label?: ReactNode;
  description?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Red border on the control. Set by the control from its resolved error. */
  invalid?: boolean;
  /** The error message. Wins over `successMessage`. */
  error?: ReactNode;
  /** The inline-save "Saved!" line, shown only when there is no error. */
  successMessage?: ReactNode;
  /** Drives the 44px hit target. Anything below `lg` renders shorter than 44px. */
  size?: ControlSize;
  /** Rendered at the end of the message row — the textarea's character count. */
  messageAddon?: ReactNode;
  children: ReactNode;
}

export function Field({
  id,
  label,
  description,
  required = false,
  disabled = false,
  invalid = false,
  error,
  successMessage,
  size = 'md',
  messageAddon,
  className,
  children,
  ...rest
}: FieldProps) {
  const message = error ?? successMessage;
  // Remounting on a new message replays the shake, so the second error of a
  // field is animated as visibly as the first.
  const messageKey = typeof message === 'string' ? message : 'field-message';

  /**
   * CONVENTIONS §6. A text input cannot carry the `dhd-target` overlay itself —
   * browsers do not render pseudo-elements on `<input>` — so the row that holds
   * the control carries it, and a press anywhere on that overlay is forwarded to
   * the control it belongs to.
   */
  function handleMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const control = event.currentTarget.querySelector<HTMLElement>('input, textarea');
    if (!control || control.contains(event.target as Node) || control.hasAttribute('disabled')) return;
    // Keep the focus we are about to set instead of letting the press blur it.
    event.preventDefault();
    control.focus();
  }

  return (
    <div
      {...rest}
      className={cn('flex w-full min-w-0 flex-col gap-1.5', className)}
      data-invalid={invalid || undefined}
    >
      {label ? (
        <Label htmlFor={id} required={required} disabled={disabled}>
          {label}
        </Label>
      ) : null}

      {description ? (
        <p id={fieldDescriptionId(id)} className="text-xs text-muted">
          {description}
        </p>
      ) : null}

      <div className={cn('relative w-full min-w-0', size !== 'lg' && 'dhd-target')} onMouseDown={handleMouseDown}>
        {children}
      </div>

      {message || messageAddon ? (
        <div className="flex items-start justify-between gap-3">
          {message ? (
            <FieldMessage key={messageKey} id={fieldMessageId(id)} tone={error ? 'error' : 'success'}>
              {message}
            </FieldMessage>
          ) : null}
          {messageAddon ? <div className="shrink-0 text-xs text-muted">{messageAddon}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
